import {
  extractAltFromCaption,
  resolveImageAltText,
} from '@/features/content/imageAltText';
import { resolveImageUnitSrc } from '@/features/content/imageUnitSrc';
import type {
  NormalizedContentUnit,
  RawContentSection,
} from '@/features/content/useComponent';
import { normalizeContentUnits } from '@/features/questions/questionNormalizer';

import type { EditableQuestionImage } from './editorTypes';

// The editor edits a question prompt as a SINGLE rich-text HTML string (the first
// text content unit). A content section may also carry IMAGE units, which the
// HTML string cannot hold. These helpers surface those images as first-class
// editable items (`EditableQuestionImage`) so they can be shown, replaced, and
// deleted in the editor — and rebuild the raw content units (text + images +
// preserved other media) for the preview and for saving.

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function str(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() !== '' ? value : undefined;
}

function unitImageUrls(unit: Record<string, unknown>) {
  return {
    dynamicImageUrl: str(unit.dynamic_image_url ?? unit.dynamicImageUrl),
    imageUrl: str(unit.image_url ?? unit.imageUrl),
  };
}

function isImageUnit(unit: Record<string, unknown>): boolean {
  const { dynamicImageUrl, imageUrl } = unitImageUrls(unit);
  return Boolean(imageUrl || dynamicImageUrl);
}

function hasText(unit: Record<string, unknown>): boolean {
  return typeof unit.text === 'string' && unit.text.trim().length > 0;
}

function contentUnits(
  section: RawContentSection | null | undefined,
): Record<string, unknown>[] {
  const raw = section?.content_units ?? section?.contentUnits;
  return (Array.isArray(raw) ? raw : []).filter(isRecord);
}

function makeId(prefix: string, index: number): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `${prefix}-image-${index}`;
}

/** Extract a prompt's IMAGE content units as editable images (in order). */
export function extractPromptImages(
  section: RawContentSection | null | undefined,
  idPrefix: string,
): EditableQuestionImage[] {
  const images: EditableQuestionImage[] = [];
  contentUnits(section).forEach((unit, index) => {
    if (!isImageUnit(unit)) {
      return;
    }
    const { dynamicImageUrl, imageUrl } = unitImageUrls(unit);
    const rawSize = unit.image_size ?? unit.imageSize;
    images.push({
      // Same resolution as a standalone image unit: the stored field, then a
      // legacy `[alt: ...]` marker in the caption.
      altText: resolveImageAltText({
        altText: str(unit.alt_text ?? unit.altText),
        caption: str(unit.caption),
      }) || undefined,
      caption: extractAltFromCaption(str(unit.caption)).displayCaption || undefined,
      dynamicImageUrl,
      id: makeId(idPrefix, index),
      imageSize:
        typeof rawSize === 'number' || typeof rawSize === 'string'
          ? rawSize
          : undefined,
      imageUrl,
    });
  });
  return images;
}

/** The display URL for a prompt image (dynamic serving URL with size suffix, or raw). */
export function promptImageSrc(image: EditableQuestionImage): string {
  return (
    resolveImageUnitSrc({
      dynamic_image_url: image.dynamicImageUrl,
      image_size: image.imageSize,
      image_url: image.imageUrl,
    }) ?? ''
  );
}

function imageToRawUnit(image: EditableQuestionImage): Record<string, unknown> {
  const unit: Record<string, unknown> = { type: 2 };
  if (image.altText) {
    unit.alt_text = image.altText;
  }
  if (image.caption) {
    unit.caption = image.caption;
  }
  if (image.imageUrl) {
    unit.image_url = image.imageUrl;
  }
  if (image.dynamicImageUrl) {
    unit.dynamic_image_url = image.dynamicImageUrl;
  }
  if (image.imageSize !== undefined) {
    unit.image_size = image.imageSize;
  }
  return unit;
}

// Non-text, non-image units (iframe/video) are rare in question prompts but must
// not be dropped on save; they are preserved from the original section.
function preservedOtherUnits(
  section: RawContentSection | null | undefined,
): Record<string, unknown>[] {
  return contentUnits(section).filter(
    (unit) => !hasText(unit) && !isImageUnit(unit),
  );
}

/**
 * Rebuild a prompt's raw content units from the edited text and the (edited)
 * image list: the text unit first, then the images, then any preserved
 * non-text/non-image units. This honors image additions and deletions made in
 * the editor while never dropping other media.
 */
export function buildPromptContentUnits(
  editedHtml: string,
  images: EditableQuestionImage[],
  sourceSection: RawContentSection | null | undefined,
): Record<string, unknown>[] {
  // Preserve the original text unit's metadata (id, etc.) while updating its html.
  const firstText = contentUnits(sourceSection).find(hasText) ?? {};
  return [
    { ...firstText, text: editedHtml, type: 1 },
    ...images.map(imageToRawUnit),
    ...preservedOtherUnits(sourceSection),
  ];
}

/**
 * The normalized units to render in the edit-mode preview: the edited text plus
 * the prompt images, in order, via the display normalizer so images resolve
 * exactly as they do for students.
 */
export function buildPromptPreviewUnits(
  editedHtml: string,
  images: EditableQuestionImage[],
): NormalizedContentUnit[] {
  return normalizeContentUnits(
    {
      content_units: [
        { text: editedHtml, type: 1 },
        ...images.map(imageToRawUnit),
      ],
    },
    [],
  );
}

/**
 * The file name to show for a prompt image.
 *
 * A Firebase download URL is a wall of percent-encoded path plus a token, which
 * tells an instructor nothing about which picture they are looking at. The last
 * path segment, decoded, is the name they uploaded.
 */
export function promptImageFileName(image: EditableQuestionImage): string {
  const source = image.imageUrl ?? image.dynamicImageUrl ?? '';

  if (!source) {
    return 'Image';
  }

  // Drop the query string first: a token can itself contain slashes.
  const withoutQuery = source.split('?')[0];
  const lastSegment = withoutQuery.split('/').pop() ?? '';

  let decoded = lastSegment;
  try {
    decoded = decodeURIComponent(lastSegment);
  } catch {
    // A malformed escape is not worth failing over; the raw segment still
    // reads better than the whole URL.
  }

  // Firebase keeps the full storage path in that segment (`images/uploads/x.png`
  // percent-encoded), so take the tail again once decoded.
  const fileName = decoded.split('/').pop() ?? '';

  return fileName || 'Image';
}
